import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useMemo } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { Box, Column, ConfirmationModal, Divider, Row, TextButton, Typography } from 'seagull';
import { PlusCircle } from 'seagull/icons';
import { ClientDetailsForm } from '~/components/ClientDetailsForm/ClientDetailsForm';
import { CLIENT_DETAILS_FORM_DEFAULTS } from '~/components/ClientDetailsForm/ClientDetailsForm.utils';
import { ContactAccountingMapping } from '~/components/ContactForm/ContactAccountingMapping';
import { ContactForm } from '~/components/ContactForm/ContactForm';
import { CONTACT_FORM_DEFAULTS } from '~/components/ContactForm/ContactForm.utils';
import { useHasLinkedAccounting } from '~/components/ContactForm/hooks/useHasLinkedAccounting';
import { useLocaleKeys } from '~/i18n';
import styles from './CreateClientModal.module.scss';
import { CLIENT_DETAILS_FORM_TEST_ID, CONTACT_FORM_TEST_ID, CONTACTS_FIELD_NAME, createClientModalTextsPath, GetCreateClientModalTID } from './CreateClientModal.utils';
import { useCreateClientFlow } from './hooks/useCreateClientFlow';
const MODAL_WIDTH = '700px';
const MODAL_HEIGHT = '972px';
const MODAL_MAX_HEIGHT = '90vh';
const MAX_CONTACTS = 10;
export function CreateClientModal(param) {
    let { dismissModal, onCreated, testId } = param;
    const { t } = useLocaleKeys(createClientModalTextsPath);
    const { CreateClientModalTestIds } = useMemo(()=>GetCreateClientModalTID(testId), [
        testId
    ]);
    const methods = useForm({
        mode: 'all',
        defaultValues: _object_spread_props(_object_spread({}, CLIENT_DETAILS_FORM_DEFAULTS), {
            [CONTACTS_FIELD_NAME]: [
                CONTACT_FORM_DEFAULTS
            ]
        })
    });
    const { fields, append, remove } = useFieldArray({
        control: methods.control,
        name: CONTACTS_FIELD_NAME
    });
    const { submit, hasClientError } = useCreateClientFlow(dismissModal, onCreated);
    const hasLinkedAccounting = useHasLinkedAccounting();
    const hasReachedContactLimit = fields.length >= MAX_CONTACTS;
    const appendContact = ()=>{
        if (hasReachedContactLimit) return;
        append(CONTACT_FORM_DEFAULTS);
    };
    const onValid = (values)=>submit({
            clientType: values.clientType,
            name: values.name,
            contacts: values.contacts.map((contact)=>({
                    firstName: contact.firstName,
                    lastName: contact.lastName,
                    email: contact.email,
                    phone: contact.phone,
                    accounting: {
                        qbo: contact.qbo,
                        xero: contact.xero
                    }
                }))
        });
    const modalProps = {
        title: t.title(),
        primaryButtonLabel: t.createButton(),
        secondaryButtonLabel: t.cancelButton()
    };
    return /*#__PURE__*/ _jsx(FormProvider, _object_spread_props(_object_spread({}, methods), {
        children: /*#__PURE__*/ _jsx(ConfirmationModal, _object_spread_props(_object_spread({}, modalProps), {
            testId: CreateClientModalTestIds.Root(),
            width: MODAL_WIDTH,
            height: MODAL_HEIGHT,
            maxHeight: MODAL_MAX_HEIGHT,
            textAlignCenter: false,
            bodyClassName: styles.bodyFill,
            contentClassName: styles.content,
            bi: {
                primaryButton: {
                    name: 'create_client_submit'
                },
                secondaryButton: {
                    name: 'create_client_cancel'
                },
                closeButton: {
                    name: 'create_client_close'
                }
            },
            primaryButtonTestId: CreateClientModalTestIds.CreateButton(),
            secondaryButtonTestId: CreateClientModalTestIds.CancelButton(),
            onPrimaryButtonClick: methods.handleSubmit(onValid),
            onSecondaryButtonClick: dismissModal,
            onCloseButtonClick: dismissModal,
            content: /*#__PURE__*/ _jsxs(Column, {
                gap: 24,
                children: [
                    /*#__PURE__*/ _jsx(ClientDetailsForm, {
                        testId: CLIENT_DETAILS_FORM_TEST_ID
                    }),
                    fields.map((field, index)=>{
                        const isPrimary = index === 0;
                        return /*#__PURE__*/ _jsxs(Fragment, {
                            children: [
                                !isPrimary && /*#__PURE__*/ _jsx(Divider, {
                                    margin: 0
                                }),
                                /*#__PURE__*/ _jsx(ContactForm, {
                                    namePrefix: "".concat(CONTACTS_FIELD_NAME, ".").concat(index),
                                    title: t.contactTitle(),
                                    showRemove: fields.length > 1,
                                    onRemove: ()=>remove(index),
                                    accountingMapping: hasLinkedAccounting ? /*#__PURE__*/ _jsx(ContactAccountingMapping, {
                                        namePrefix: "".concat(CONTACTS_FIELD_NAME, ".").concat(index)
                                    }) : undefined,
                                    testId: CONTACT_FORM_TEST_ID(index)
                                })
                            ]
                        }, field.id);
                    }),
                    /*#__PURE__*/ _jsx(Row, {
                        children: /*#__PURE__*/ _jsx(TextButton, {
                            variant: "paragraph2",
                            skin: "primary",
                            weight: "bold",
                            underline: "none",
                            prefixIcon: /*#__PURE__*/ _jsx(PlusCircle, {}),
                            onClick: appendContact,
                            disabled: hasReachedContactLimit,
                            testId: CreateClientModalTestIds.AddContactButton(),
                            bi: {
                                name: 'add_contact'
                            },
                            children: t.addContactAction()
                        })
                    }),
                    hasClientError && /*#__PURE__*/ _jsx(Box, {
                        testId: CreateClientModalTestIds.CreateClientError(),
                        children: /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            skin: "error",
                            children: t.createClientError()
                        })
                    })
                ]
            })
        }))
    }));
}
